/**
 * Overview Section
 *
 * KPI stat cards + DAU line chart + events bar chart.
 * Adapted from glass-card design system.
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Users, Activity, FileText, UserPlus, Clock, Zap,
} from 'lucide-react';
import type { AnalyticsOverview, TrendDataPoint } from '../../../types/analytics';
import { StatCardSkeleton, ChartSkeleton } from './AnalyticsSkeleton';

// --- Stat Card ---

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  change?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, color, bgColor, change }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={`p-1.5 rounded-lg ${bgColor}`}>
        <Icon className={`w-3.5 h-3.5 ${color}`} />
      </div>
      <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wide">{label}</p>
    </div>
    <p className="text-2xl font-extrabold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    {change && (
      <p className="text-[10px] text-emerald-600 font-medium mt-0.5">{change}</p>
    )}
  </motion.div>
);

// --- Main ---

interface OverviewSectionProps {
  overview: AnalyticsOverview | null;
  trends: TrendDataPoint[] | null;
  loading: boolean;
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({
  overview,
  trends,
  loading,
}) => {
  if (loading || !overview) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
        <ChartSkeleton />
        <ChartSkeleton />
      </div>
    );
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    return `${Math.round(seconds / 60)}m`;
  };

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Total Users"
          value={overview.total_users}
          icon={Users}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <StatCard
          label="DAU"
          value={overview.dau}
          icon={Activity}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <StatCard
          label="Sessions"
          value={overview.sessions}
          icon={Clock}
          color="text-amber-600"
          bgColor="bg-amber-100"
        />
        <StatCard
          label="Avg Duration"
          value={formatDuration(overview.avg_session_duration)}
          icon={Clock}
          color="text-emerald-600"
          bgColor="bg-emerald-100"
        />
        <StatCard
          label="Events"
          value={overview.total_events}
          icon={Zap}
          color="text-indigo-600"
          bgColor="bg-indigo-100"
        />
        <StatCard
          label="Signups"
          value={overview.signups}
          icon={UserPlus}
          color="text-pink-600"
          bgColor="bg-pink-100"
        />
        <StatCard
          label="Content"
          value={overview.content_generated}
          icon={FileText}
          color="text-cyan-600"
          bgColor="bg-cyan-100"
        />
        <StatCard
          label="WAU / MAU"
          value={`${overview.wau} / ${overview.mau}`}
          icon={Users}
          color="text-violet-600"
          bgColor="bg-violet-100"
        />
      </div>

      {/* DAU Trend Line Chart */}
      {trends && trends.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Daily Active Users</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
              />
              <YAxis tick={{ fontSize: 10 }} width={30} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                labelFormatter={(d) => new Date(String(d)).toLocaleDateString()}
              />
              <Line
                type="monotone"
                dataKey="dau"
                stroke="#a855f7"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#a855f7' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Sessions Bar Chart */}
      {trends && trends.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Daily Sessions</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
              />
              <YAxis tick={{ fontSize: 10 }} width={30} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              />
              <Bar dataKey="sessions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
